import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Settings2, Loader2, RotateCcw, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { TemplateAccessNote } from "@/components/TemplateAccessNote";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ops/package-renewals/drafts/config`;
const MAX = 320;
const fill = (t, f) => t.replace(/\{(\w+)\}/g, (m, k) => (k in f ? String(f[k] ?? "") : m));

const Situation = ({ k, label, cfg, value, onChange }) => {
  const preview = value ? fill(value, cfg.sample_fields[k] || {}) : cfg.smart_defaults[k];
  const over = preview.length > MAX;
  return (
    <div className="space-y-1.5" data-testid={`pkg-tpl-${k}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-gray-900">{label}</div>
        {value && cfg.can_edit && <button className="text-xs text-indigo-600 inline-flex items-center gap-1" onClick={() => onChange("")} data-testid={`pkg-tpl-reset-${k}`}><RotateCcw className="w-3 h-3" />Reset to smart default</button>}
      </div>
      <Textarea rows={3} value={value} disabled={!cfg.can_edit} onChange={(e) => onChange(e.target.value)} placeholder="Leave blank to use the smart default (shown below)" data-testid={`pkg-tpl-input-${k}`} />
      <div className={`text-xs rounded-lg p-2 ${value ? "bg-indigo-50 text-indigo-900" : "bg-gray-50 text-gray-600"}`}>
        <span className="font-bold">{value ? "Preview" : "Smart default"}:</span> {preview}
        <span className={`ml-2 font-bold ${over ? "text-rose-600" : "text-gray-400"}`} data-testid={`pkg-tpl-count-${k}`}>{preview.length}/{MAX}{over ? " — will be cut off" : ""}</span>
      </div>
    </div>
  );
};

// Package Renewals → per-location message templates + single-day daycare rate override.
export const PackageMessageConfig = () => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [tpl, setTpl] = useState({});
  const [rate, setRate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    axios.get(API).then((r) => { setCfg(r.data); setTpl(r.data.templates || {}); setRate(r.data.single_day_rate_override ?? ""); }).catch(() => toast.error("Could not load templates"));
  }, [open]);
  const refreshPrices = async () => {
    try {
      await axios.post(API.replace("/config", "/refresh-prices"));
      toast.success("Scanning Gingr POS for prices — usually under a minute");
      setCfg((c) => ({ ...c, price_scan_running: true }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start price scan"); }
  };
  useEffect(() => {
    if (!open || !cfg?.price_scan_running) return undefined;
    const t = setTimeout(() => axios.get(API).then((r) => {
      setCfg(r.data);
      if (!r.data.price_scan_running) toast.success(r.data.prices?.length ? "Package prices updated" : "Scan finished — no package prices found");
    }).catch(() => {}), 10000);
    return () => clearTimeout(t);
  }, [open, cfg]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(API, { templates: tpl, single_day_rate_override: rate === "" ? null : Number(rate) });
      setCfg(r.data); toast.success("Message templates saved — new drafts use them"); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="pkg-msg-config-btn"><Settings2 className="w-4 h-4 mr-2" />Message templates</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="ops-v3 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="pkg-msg-config-dialog">
          <DialogHeader>
            <DialogTitle>Package renewal messages</DialogTitle>
            <DialogDescription>For this location only. Leave a box blank to use the smart default with prices and savings from Gingr.</DialogDescription>
          </DialogHeader>
          {!cfg ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : (
            <div className="space-y-4">
              <TemplateAccessNote canEdit={cfg.can_edit} testid="pkg-tpl-access" />
              <div className="rounded-lg border p-3 text-sm flex flex-wrap items-center gap-3" data-testid="pkg-rate-box">
                <div className="flex-1 min-w-[220px]"><b>Single-day daycare rate</b>
                  <div className="text-xs text-gray-500">From Gingr: {cfg.auto_single_day_rate ? `$${cfg.auto_single_day_rate}` : "not found yet"} · used for "vs by the day" savings.
                    {cfg.prices?.length ? ` Packages: ${cfg.prices.map((p) => `${p.size} ${p.kind} $${p.price}`).join(" · ")}` : " Package prices not loaded yet."}</div></div>
                <div className="w-full flex flex-wrap items-center gap-2 text-xs text-gray-500" data-testid="pkg-prices-scan-row">
                  <span data-testid="pkg-prices-fetched-at">Prices last scanned: {cfg.prices_fetched_at ? new Date(cfg.prices_fetched_at).toLocaleString() : "never"}</span>
                  {cfg.can_edit && <Button size="sm" variant="outline" className="h-7" disabled={cfg.price_scan_running} onClick={refreshPrices} data-testid="pkg-refresh-prices-btn">
                    {cfg.price_scan_running ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}{cfg.price_scan_running ? "Scanning Gingr POS…" : "Refresh prices now"}</Button>}
                </div>
                <label className="text-xs text-gray-600 flex items-center gap-1.5">Override $<Input type="number" step="0.01" className="w-24 h-8" value={rate} disabled={!cfg.can_edit} onChange={(e) => setRate(e.target.value)} placeholder={cfg.auto_single_day_rate || ""} data-testid="pkg-rate-override" /></label>
              </div>
              <div className="text-xs text-gray-500">Fields you can use: {cfg.fields.map((f) => <code key={f} className="mr-1.5 bg-gray-100 rounded px-1">{`{${f}}`}</code>)}</div>
              {Object.entries(cfg.situations).map(([k, label]) => (
                <Situation key={k} k={k} label={label} cfg={cfg} value={tpl[k] || ""} onChange={(v) => setTpl({ ...tpl, [k]: v })} />
              ))}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={saving || !cfg.can_edit} data-testid="pkg-msg-config-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
